import { AdminHeader } from "@/components/AdminHeader";
import { useEffect, useState } from "react";
import { api, errMsg, fmtMoney } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Fuel } from "lucide-react";

const CATEGORIES = ["SUV", "Sedan", "Compact", "Convertible", "Van", "Luxury", "Truck"];
const FUEL_LEVELS = ["Full", "3/4", "1/2", "1/4"];
const EMPTY = { name: "", category: "SUV", seats: 5, luggage: 2, transmission: "Automatic", price_per_day: "", image_url: "", description: "", features: "", fuel_level: "Full", tank_capacity: "", vin: "", active: true, odometer_miles: "", next_service_miles: "", next_service_date: "" };
const ROW = "border-[#E5DED5] hover:bg-[#F5EFE6]/50 transition-colors";

export default function VehiclesTab() {
  const [vehicles, setVehicles] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/vehicles").then((r) => setVehicles(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (v) => {
    setEditing(v);
    setForm({ fuel_level: "Full", ...v, features: (v.features || []).join(", ") });
    setOpen(true);
  };

  const save = async () => {
    if (!form.name || !form.price_per_day) return toast.error("Name and daily price are required");
    setSaving(true);
    const payload = {
      name: form.name,
      category: form.category,
      seats: Number(form.seats),
      luggage: Number(form.luggage || 0),
      transmission: form.transmission,
      price_per_day: Number(form.price_per_day),
      image_url: form.image_url,
      description: form.description,
      features: form.features ? form.features.split(",").map((f) => f.trim()).filter(Boolean) : [],
      fuel_level: form.fuel_level,
      tank_capacity: form.tank_capacity === "" || form.tank_capacity == null ? null : Number(form.tank_capacity),
      odometer_miles: form.odometer_miles === "" || form.odometer_miles == null ? null : Number(form.odometer_miles),
      next_service_miles: form.next_service_miles === "" || form.next_service_miles == null ? null : Number(form.next_service_miles),
      next_service_date: form.next_service_date || "",
      vin: form.vin || "",
      active: !!form.active,
    };
    try {
      if (editing) await api.put(`/admin/vehicles/${editing.id}`, payload);
      else await api.post("/admin/vehicles", payload);
      toast.success(editing ? "Vehicle updated" : "Vehicle added");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (v) => {
    if (!window.confirm(`Remove ${v.name} from the fleet?`)) return;
    try {
      await api.delete(`/admin/vehicles/${v.id}`);
      toast.success("Vehicle removed");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const toggleLot = async (v) => {
    const next = !(v.on_lot ?? true);
    try {
      await api.patch(`/admin/vehicles/${v.id}/lot`, { on_lot: next });
      toast.success(next ? `${v.name} added back on lot` : `${v.name} removed from lot`);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div data-testid="admin-vehicles-tab">
      <AdminHeader testid="admin-header-vehicles" eyebrow="Fleet" title="Vehicles" description={`${vehicles.length} vehicles in fleet`} actions={<Button data-testid="vehicle-add-button" onClick={openNew} className="rounded-full bg-[#C71E2E] text-white hover:bg-[#A01825] font-ui hover:scale-95 transition-transform"><Plus size={15} className="mr-2" /> Add Vehicle</Button>} />

      <div className="bg-white shadow-[0_8px_30px_rgba(0,0,0,0.04)] rounded-2xl overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-[#E5DED5] hover:bg-transparent">
              <TableHead>Vehicle</TableHead>
              <TableHead>Category</TableHead>
              <TableHead>Rate / Day</TableHead>
              <TableHead>Fuel Out</TableHead>
              <TableHead>On Lot</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {vehicles.map((v) => (
              <TableRow key={v.id} data-testid={`admin-vehicle-row-${v.id}`} className={ROW}>
                <TableCell className="font-ui font-medium">{v.name}
                  {((v.next_service_miles && v.odometer_miles && Number(v.odometer_miles) >= Number(v.next_service_miles) - 500) || (v.next_service_date && v.next_service_date <= new Date().toISOString().slice(0, 10))) && (
                    <span data-testid={`vehicle-service-due-${v.id}`} className="ml-2 inline-block rounded-full border border-amber-300 bg-amber-50 text-amber-700 text-[10px] font-ui font-semibold px-2 py-0.5 align-middle">Service due</span>
                  )}
                </TableCell>
                <TableCell className="text-muted-foreground">{v.category}</TableCell>
                <TableCell className="text-[#C71E2E] font-ui">{fmtMoney(v.price_per_day)}</TableCell>
                <TableCell data-testid={`vehicle-fuel-${v.id}`}>
                  <span className="inline-flex items-center gap-1.5 text-xs font-ui text-muted-foreground">
                    <Fuel size={12} className="text-[#C71E2E]" /> {v.fuel_level || "Full"}
                  </span>
                </TableCell>
                <TableCell data-testid={`vehicle-lot-toggle-${v.id}`}>
                  <div className="flex items-center gap-2">
                    <Switch checked={v.on_lot ?? true} onCheckedChange={() => toggleLot(v)} />
                    <span className={`text-[10px] font-ui tracking-[0.1em] uppercase whitespace-nowrap ${(v.on_lot ?? true) ? "text-emerald-700" : "text-muted-foreground"}`}>{(v.on_lot ?? true) ? "On lot" : "Off lot"}</span>
                  </div>
                </TableCell>
                <TableCell>
                  <span className={`text-[10px] font-ui tracking-[0.15em] uppercase border rounded-full px-3 py-1 ${v.active ? "text-emerald-700 border-emerald-200 bg-emerald-50" : "text-muted-foreground border-[#E5DED5] bg-[#FAF6F0]"}`}>
                    {v.active ? "Active" : "Hidden"}
                  </span>
                </TableCell>
                <TableCell className="text-right">
                  <button data-testid={`vehicle-edit-button-${v.id}`} onClick={() => openEdit(v)} className="text-muted-foreground hover:text-[#C71E2E] transition-colors duration-200 mr-4" aria-label="Edit">
                    <Pencil size={15} />
                  </button>
                  <button data-testid={`vehicle-delete-button-${v.id}`} onClick={() => remove(v)} className="text-muted-foreground hover:text-red-700 transition-colors duration-200" aria-label="Delete">
                    <Trash2 size={15} />
                  </button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white border-[#E5DED5] text-foreground max-w-lg max-h-[90vh] overflow-y-auto rounded-2xl">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl tracking-tight">{editing ? "Edit Vehicle" : "Add Vehicle"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label className="font-ui text-xs text-muted-foreground">Name</Label>
              <Input data-testid="vehicle-form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Range Rover Sport" className="input-dark mt-1.5" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="font-ui text-xs text-muted-foreground">Category</Label>
                <Select value={form.category} onValueChange={(val) => setForm({ ...form, category: val })}>
                  <SelectTrigger data-testid="vehicle-form-category" className="input-dark mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white border-[#E5DED5]">
                    {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="font-ui text-xs text-muted-foreground">Seats</Label>
                <Input data-testid="vehicle-form-seats" type="number" min={1} value={form.seats} onChange={(e) => setForm({ ...form, seats: e.target.value })} className="input-dark mt-1.5" />
              </div>
              <div>
                <Label className="font-ui text-xs text-muted-foreground">Luggage (bags)</Label>
                <Input data-testid="vehicle-form-luggage" type="number" min={0} value={form.luggage ?? 2} onChange={(e) => setForm({ ...form, luggage: e.target.value })} className="input-dark mt-1.5" />
              </div>
              <div>
                <Label className="font-ui text-xs text-muted-foreground">Transmission</Label>
                <Select value={form.transmission} onValueChange={(val) => setForm({ ...form, transmission: val })}>
                  <SelectTrigger data-testid="vehicle-form-transmission" className="input-dark mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white border-[#E5DED5]">
                    <SelectItem value="Automatic">Automatic</SelectItem>
                    <SelectItem value="Manual">Manual</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="font-ui text-xs text-muted-foreground">Fuel level at pickup</Label>
                <Select value={form.fuel_level || "Full"} onValueChange={(val) => setForm({ ...form, fuel_level: val })}>
                  <SelectTrigger data-testid="vehicle-form-fuel-level" className="input-dark mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white border-[#E5DED5]">
                    {FUEL_LEVELS.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="font-ui text-xs text-muted-foreground">Fuel tank capacity (gal)</Label>
                <Input data-testid="vehicle-form-tank-capacity" type="number" min={0} step="0.1" value={form.tank_capacity ?? ""} onChange={(e) => setForm({ ...form, tank_capacity: e.target.value })} placeholder="e.g. 14" className="input-dark mt-1.5" />
              </div>
              <div>
                <Label className="font-ui text-xs text-muted-foreground">Odometer (miles)</Label>
                <Input data-testid="vehicle-form-odometer" type="number" min={0} value={form.odometer_miles ?? ""} onChange={(e) => setForm({ ...form, odometer_miles: e.target.value })} placeholder="e.g. 42000" className="input-dark mt-1.5" />
              </div>
              <div>
                <Label className="font-ui text-xs text-muted-foreground">Next service at (miles)</Label>
                <Input data-testid="vehicle-form-next-service-miles" type="number" min={0} value={form.next_service_miles ?? ""} onChange={(e) => setForm({ ...form, next_service_miles: e.target.value })} placeholder="e.g. 45000" className="input-dark mt-1.5" />
              </div>
              <div>
                <Label className="font-ui text-xs text-muted-foreground">Next service date</Label>
                <Input data-testid="vehicle-form-next-service-date" type="date" value={form.next_service_date || ""} onChange={(e) => setForm({ ...form, next_service_date: e.target.value })} className="input-dark mt-1.5" />
              </div>
              <div>
                <Label className="font-ui text-xs text-muted-foreground">Price per day (USD)</Label>
                <Input data-testid="vehicle-form-price" type="number" min={1} step="0.01" value={form.price_per_day} onChange={(e) => setForm({ ...form, price_per_day: e.target.value })} className="input-dark mt-1.5" />
              </div>
              <div>
                <Label className="font-ui text-xs text-muted-foreground">VIN number (for contract barcode)</Label>
                <Input data-testid="vehicle-form-vin" value={form.vin || ""} onChange={(e) => setForm({ ...form, vin: e.target.value.toUpperCase() })} placeholder="1HGBH41JXMN109186" maxLength={17} className="input-dark mt-1.5" />
              </div>
            </div>
            <div>
              <Label className="font-ui text-xs text-muted-foreground">Image URL</Label>
              <Input data-testid="vehicle-form-image" value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} placeholder="https://…" className="input-dark mt-1.5" />
            </div>
            <div>
              <Label className="font-ui text-xs text-muted-foreground">Description</Label>
              <Textarea data-testid="vehicle-form-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="input-dark mt-1.5" />
            </div>
            <div>
              <Label className="font-ui text-xs text-muted-foreground">Features (comma separated)</Label>
              <Input data-testid="vehicle-form-features" value={form.features} onChange={(e) => setForm({ ...form, features: e.target.value })} placeholder="Bluetooth, Backup camera, Apple CarPlay" className="input-dark mt-1.5" />
            </div>
            <div className="flex items-center gap-3">
              <Switch data-testid="vehicle-form-active" checked={form.active} onCheckedChange={(val) => setForm({ ...form, active: val })} />
              <Label className="font-ui text-xs text-muted-foreground">Visible on website</Label>
            </div>
            <Button data-testid="vehicle-submit-button" onClick={save} disabled={saving} className="w-full rounded-full bg-[#C71E2E] text-white hover:bg-[#A01825] font-ui">
              {saving ? "Saving…" : editing ? "Save Changes" : "Add Vehicle"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
